// src/pages/FrameIQList.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";

const FrameIQList = () => {
  // ==================== STATE MANAGEMENT ====================

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [limit, setLimit] = useState(100);
  
  // Modal state - for content viewing
  const [modalContent, setModalContent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_URL;

  // Character limit for truncation
  const CHAR_LIMIT = 30;

  // ==================== EFFECTS ====================

  useEffect(() => {
    fetchCampaigns();
  }, [currentPage, limit]);

  // ==================== HANDLERS ====================

  // Fetch campaigns from API
  const fetchCampaigns = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${BASE_URL}/frame_iq/api/campaign_detail/list`, {
        params: {
          page: currentPage,
          limit: limit
        }
      });

      if (response.data.success) {
        setCampaigns(response.data.data || []);
        setTotalCampaigns(response.data.total || 0);
      } else {
        toast.error('Failed to fetch campaigns');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Error loading campaigns: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Open modal with any content
  const handleViewContent = (title, content, campaignName) => {
    setModalContent({ title, content, campaignName });
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setModalContent(null);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCampaignToDelete(null);
  };

  // Confirm delete campaign
  const handleConfirmDelete = async () => {
    if (!campaignToDelete) return;

    setDeleting(true);

    try {
      const response = await axios.delete(
        `${BASE_URL}/frame_iq/api/campaign/delete/${campaignToDelete.id}`
      );

      if (response.data.success) {
        toast.success('Campaign deleted successfully!');
        
        // Remove from local state
        setCampaigns(campaigns.filter(c => c.id !== campaignToDelete.id));
        setTotalCampaigns(totalCampaigns - 1);
        
        // Close modal
        handleCloseDeleteModal();
        
        // If current page is now empty and not page 1, go to previous page
        if (campaigns.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } else {
        toast.error('Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Error deleting campaign: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleting(false);
    }
  };

  // Toggle status - ACTIVE/INACTIVE
  const handleToggleStatus = async (campaignId, currentStatus) => {
    const newStatus = String(currentStatus) === '1' ? '0' : '1';
    
    try {
      // Show optimistic update
      setCampaigns(campaigns.map(c => 
        c.id === campaignId ? { ...c, status: newStatus } : c
      ));

      // Call backend API
      const response = await axios.put(
        `${BASE_URL}/frame_iq/api/campaign/status/${campaignId}`,
        { status: newStatus }
      );

      if (response.data.success) {
        toast.success(`Campaign ${newStatus === '1' ? 'activated' : 'deactivated'} successfully!`);
      } else {
        // Revert on failure
        setCampaigns(campaigns.map(c => 
          c.id === campaignId ? { ...c, status: currentStatus } : c
        ));
        toast.error('Failed to update status');
      }
    } catch (error) {
      // Revert on error
      setCampaigns(campaigns.map(c => 
        c.id === campaignId ? { ...c, status: currentStatus } : c
      ));
      console.error('Error updating status:', error);
      toast.error('Error updating status: ' + error.message);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCampaigns / limit);

  // Get status color
  const getStatusColor = (status) => {
    const statusStr = String(status);
    return statusStr === '1' ? 'bg-green-500' : 'bg-gray-300';
  };

  // Truncate text helper
  const truncateText = (text, limit = CHAR_LIMIT) => {
    if (!text) return 'N/A';
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  };

  // Check if text needs "View All" button
  const needsViewAll = (text) => {
    return text && text.length > CHAR_LIMIT;
  };

  // ==================== RENDER ====================

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster position="top-right" />

      {/* Header - ALL LEFT ALIGNED */}
      <div className="mb-6">
        <div className="flex flex-col items-start gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Campaign List</h1>
            <p className="text-sm text-gray-600 mt-1">Total Campaigns: {totalCampaigns}</p>
          </div>
          
          <button
            onClick={() => navigate('/campaign/create')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm whitespace-nowrap"
          >
            + New Campaign
          </button>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="mb-4 bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <button
            onClick={fetchCampaigns}
            className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
            disabled={loading}
          >
            {loading ? '⟳ Loading...' : '↻ Refresh'}
          </button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows per page:</span>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
          <p className="text-gray-500">Loading campaigns...</p>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                    Campaign Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                    Signals
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ad Creative
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Language
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Audience
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Output Mode
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DV360
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remarks
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="font-medium">No campaigns found</p>
                        <p className="text-sm text-gray-400 mt-1">Create your first campaign to get started</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign, index) => (
                    <tr key={campaign.id || index} className="hover:bg-gray-50 transition-colors">
                      {/* Campaign Name - Sticky */}
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[200px]">
                            {truncateText(campaign.brief_name, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.brief_name) && (
                            <button
                              onClick={() => handleViewContent('Campaign Name', campaign.brief_name, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Keywords - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {campaign.keywords && campaign.keywords.trim() ? (
                          <div className="flex items-center gap-2">
                            <span className="truncate max-w-xs">
                              {truncateText(campaign.keywords, 50)}
                            </span>
                            {needsViewAll(campaign.keywords) && (
                              <button
                                onClick={() => handleViewContent('Signals', campaign.keywords, campaign.brief_name)}
                                className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                              >
                                View All
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-xs">No signals</span>
                        )}
                      </td>

                      {/* Ad Creative Link */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {campaign.ad_creative && campaign.ad_creative.trim() ? (
                          <a 
                            href={campaign.ad_creative} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            title={campaign.ad_creative}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            View
                          </a>
                        ) : (
                          <span className="text-gray-400 italic text-xs">No link</span>
                        )}
                      </td>

                      {/* Status - TOGGLE SWITCH */}
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleToggleStatus(campaign.id, campaign.status)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                              String(campaign.status) === '1' 
                                ? 'bg-green-500' 
                                : 'bg-gray-300'
                            }`}
                            role="switch"
                            aria-checked={String(campaign.status) === '1'}
                            title={`Click to ${String(campaign.status) === '1' ? 'deactivate' : 'activate'}`}
                          >
                            <span
                              aria-hidden="true"
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                String(campaign.status) === '1' 
                                  ? 'translate-x-5' 
                                  : 'translate-x-0'
                              }`}
                            />
                          </button>
                          
                          <span className={`text-sm font-medium ${
                            String(campaign.status) === '1' 
                              ? 'text-green-600' 
                              : 'text-gray-600'
                          }`}>
                            {String(campaign.status) === '1' ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>

                      {/* Language - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[120px]">
                            {truncateText(campaign.language, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.language) && (
                            <button
                              onClick={() => handleViewContent('Language', campaign.language, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Country - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[120px]">
                            {truncateText(campaign.geo_country, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.geo_country) && (
                            <button
                              onClick={() => handleViewContent('Country', campaign.geo_country, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* City - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[120px]">
                            {truncateText(campaign.city, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.city) && (
                            <button
                              onClick={() => handleViewContent('City', campaign.city, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Audience - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[120px]">
                            {truncateText(campaign.audience, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.audience) && (
                            <button
                              onClick={() => handleViewContent('Audience', campaign.audience, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Created Date */}
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {campaign.inserted_date || 'N/A'}
                      </td>

                      {/* Output Mode - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[120px]">
                            {truncateText(campaign.output_mode, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.output_mode) && (
                            <button
                              onClick={() => handleViewContent('Output Mode', campaign.output_mode, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* DV360 - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[120px]">
                            {truncateText(campaign.dv360_config_name, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.dv360_config_name) && (
                            <button
                              onClick={() => handleViewContent('DV360 Config', campaign.dv360_config_name, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Remarks - WITH MODAL */}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[120px]">
                            {truncateText(campaign.remarks, CHAR_LIMIT)}
                          </span>
                          {needsViewAll(campaign.remarks) && (
                            <button
                              onClick={() => handleViewContent('Remarks', campaign.remarks, campaign.brief_name)}
                              className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap font-medium hover:underline"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Actions - DELETE BUTTON */}
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleDeleteClick(campaign)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                          title="Delete campaign"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination - ALL LEFT ALIGNED */}
      {!loading && totalPages > 1 && (
        <div className="mt-4 bg-white rounded-lg shadow p-4">
          <div className="flex flex-col items-start gap-4">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * limit, totalCampaigns)}</span> of{' '}
              <span className="font-medium">{totalCampaigns}</span> campaigns
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                ◄ Previous
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 border rounded-md transition-colors text-sm ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && (
                <>
                  <span className="px-2 py-1.5 text-gray-500">...</span>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className={`px-3 py-1.5 border rounded-md transition-colors text-sm ${
                      currentPage === totalPages
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Next ►
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Legend */}
      <div className="mt-4 p-4 bg-white rounded-lg shadow">
        <p className="text-sm font-medium text-gray-700 mb-2">Status Legend:</p>
        <div className="flex gap-6 text-sm">
          <span className="flex items-center gap-2">
            <div className="w-11 h-6 bg-green-500 rounded-full flex items-center">
              <div className="w-4 h-4 bg-white rounded-full ml-6"></div>
            </div>
            <span className="text-green-600 font-medium">Active</span>
          </span>
          <span className="flex items-center gap-2">
            <div className="w-11 h-6 bg-gray-300 rounded-full flex items-center">
              <div className="w-4 h-4 bg-white rounded-full ml-1"></div>
            </div>
            <span className="text-gray-600 font-medium">Inactive</span>
          </span>
          <span className="text-gray-500 text-xs italic">Click toggle to change status</span>
        </div>
      </div>

      {/* Universal Modal - FOR ALL FIELDS */}
      {showModal && modalContent && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleCloseModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalContent.title}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Campaign: {modalContent.campaignName}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                  {modalContent.content}
                </div>
              </div>
              
              {/* Character count */}
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span><span className="font-medium text-gray-700">{modalContent.content?.length || 0}</span> characters</span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                Close
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(modalContent.content);
                  toast.success('✓ Content copied to clipboard!');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Content
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && campaignToDelete && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleCloseDeleteModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Campaign
                </h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete this campaign?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="font-medium text-gray-900">{campaignToDelete.brief_name}</p>
                <p className="text-sm text-gray-600 mt-1">ID: {campaignToDelete.id}</p>
              </div>
              <p className="text-sm text-red-600 mt-3 font-medium">
                ⚠️ This action cannot be undone!
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseDeleteModal}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm flex items-center gap-2 disabled:bg-red-400 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Campaign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrameIQList;